import { useEffect, useRef, useState } from "react";

import { CaseDeserialized } from "src/cases/queries/casesTypes";

/**
 * Returns a counter that increments when eager mode should restart polling.
 * Triggers on:
 * - Case status or sub_status change
 * - Case assigned to current user from "no assignee" (not from another user)
 */
export const useEagerModeTriggerKey = (
  caseData?: CaseDeserialized,
  currentUserId?: string,
): number => {
  const [triggerKey, setTriggerKey] = useState(0);

  const status = caseData?.data.case_properties?.status;
  const subStatus = caseData?.data.case_properties?.sub_status;
  const assigneeId = caseData?.data.assignee?.id ?? null;

  const prevStatus = useRef<string | undefined>();
  const prevSubStatus = useRef<string | undefined>();
  const prevAssigneeId = useRef<string | null | undefined>(undefined);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (caseData === undefined) return;

    if (!isInitialized.current) {
      isInitialized.current = true;
      prevStatus.current = status;
      prevSubStatus.current = subStatus;
      prevAssigneeId.current = assigneeId;
      return;
    }

    const statusChanged =
      prevStatus.current !== status || prevSubStatus.current !== subStatus;
    const assignedToMeFromNull =
      prevAssigneeId.current === null &&
      assigneeId != null &&
      assigneeId === currentUserId;

    prevStatus.current = status;
    prevSubStatus.current = subStatus;
    prevAssigneeId.current = assigneeId;

    if (statusChanged || assignedToMeFromNull) {
      setTriggerKey((k) => k + 1);
    }
  }, [status, subStatus, assigneeId, currentUserId]);

  return triggerKey;
};
